/**
 * One active filter, with the × that clears it (`.m-fchip` in the prototype's
 * modals.css). The catalogue and the Add Fixtures modal show a row of these
 * under their filter bars.
 *
 * The × carries the whole filter in its accessible name rather than a bare
 * "Remove": six chips would otherwise be six identically-named buttons, and the
 * list is exactly where someone needs to know which one they are about to clear.
 */
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import type { Theme } from '@mui/material/styles';
import { CloseIcon } from './line-icons';

interface FilterChipProps {
  /** The facet, e.g. "Competition". Named `filterKey` because `key` is React's. */
  filterKey: string;
  value: string;
  onRemove: () => void;
}

export const FilterChip = ({ filterKey, value, onRemove }: FilterChipProps) => (
  <Box
    component="span"
    sx={(theme: Theme) => ({
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      padding: '5px 6px 5px 10px',
      borderRadius: `${theme.tokens.radius.pill}px`,
      backgroundColor: theme.tokens.color.surface,
      border: `${theme.tokens.border.width} solid ${theme.tokens.color.lineStrong}`,
      fontSize: '12.5px',
      color: theme.tokens.color.ink2,
    })}
  >
    <Box
      component="span"
      sx={(theme: Theme) => ({ color: theme.tokens.color.ink4, fontWeight: 500 })}
    >
      {filterKey}
    </Box>
    <Box
      component="span"
      sx={(theme: Theme) => ({ color: theme.tokens.color.ink, fontWeight: 600 })}
    >
      {value}
    </Box>
    <ButtonBase
      onClick={onRemove}
      aria-label={`Remove ${filterKey} filter: ${value}`}
      sx={(theme: Theme) => ({
        display: 'grid',
        placeItems: 'center',
        width: '18px',
        height: '18px',
        flexShrink: 0,
        borderRadius: '50%',
        color: theme.tokens.color.ink4,
        transition: 'background 0.12s',
        '&:hover': { backgroundColor: theme.tokens.color.bg, color: theme.tokens.color.ink2 },
      })}
    >
      <CloseIcon sx={{ fontSize: '12px' }} />
    </ButtonBase>
  </Box>
);
